// Returns binary .osz files for loadBeatmapSet via cascading mirror fallback.

type MirrorBuilder = (beatmapsetId: number, video: boolean) => string;

interface Mirror {
  name:    string;
  makeUrl: MirrorBuilder;
}

const MIRRORS: Mirror[] = [
  {
    name:    'osu.direct',
    // noVideo strips video; same param semantics as Nerinyan, ~5.6 MB stripped vs ~20 MB full on id 4651.
    makeUrl: (id, video) => `https://osu.direct/api/d/${id}${video ? '' : '?noVideo=1'}`,
  },
  {
    name:    'Nerinyan',
    // Backend is currently flaky (~40% of requests return a fake HTTP 404 after a ~5 s upstream
    // timeout); kept as a fallback because the cascade gives us a free retry on a different host.
    // Catboy + Sayobot were dropped — Catboy was network-failing from-browser and Sayobot 302s
    // to a CDN that doesn't serve CORS, so neither was usable from the page.
    makeUrl: (id, video) => `https://api.nerinyan.moe/d/${id}${video ? '' : '?noVideo=1'}`,
  },
];

const TIMEOUT_MS = 20_000;

export interface MirrorResult {
  mirror: string;
  buffer: ArrayBuffer;
}

/**
 * Resolves a beatmap MD5 (from a .osr) to its beatmapset id via osu.direct's public md5
 * index — no OAuth, so the manual-upload path stays login-free. Throws when the map isn't
 * indexed (e.g. very new/unsubmitted maps), signalling the caller to ask for the .osz.
 */
export async function lookupBeatmapsetIdByHash(hash: string): Promise<number> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    const resp = await fetch(`https://osu.direct/api/v2/md5/${hash}`, {
      headers: { accept: 'application/json' },
      signal: controller.signal,
    });
    if (!resp.ok) {
      throw new Error(`couldn't find this beatmap online (HTTP ${resp.status}) — add the .osz to load it`);
    }
    const data = await resp.json() as { beatmapset_id?: number };
    if (typeof data.beatmapset_id !== 'number') {
      throw new Error("couldn't find this beatmap online — add the .osz to load it");
    }
    return data.beatmapset_id;
  } finally {
    clearTimeout(timer);
  }
}

/** `video: true` asks the mirror for the full archive including the beatmap video (several times the size). */
export async function downloadBeatmapSet(
  beatmapsetId: number,
  onProgress?: (mirror: string) => void,
  opts: { video?: boolean } = {},
): Promise<MirrorResult> {
  const errors: string[] = [];

  for (const m of MIRRORS) {
    if (onProgress !== undefined) onProgress(m.name);
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    try {
      const resp = await fetch(m.makeUrl(beatmapsetId, opts.video === true), { signal: controller.signal });
      if (!resp.ok) {
        errors.push(`${m.name}: HTTP ${resp.status}`);
        continue;
      }
      const buffer = await resp.arrayBuffer();
      if (buffer.byteLength < 1024) {
        errors.push(`${m.name}: response too small (${buffer.byteLength} bytes)`);
        continue;
      }
      return { mirror: m.name, buffer };
    } catch (e) {
      errors.push(`${m.name}: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      clearTimeout(timer);
    }
  }

  throw new Error(`all mirrors failed for beatmapset ${beatmapsetId} — ${errors.join(' | ')}`);
}
